import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import api from "../utils/api";
import { buildMachineColorMap } from "../utils/machineColors";
import { getClientMachines } from "../controllers/machineController";

const historicalCache = new Map();
const summaryCache = new Map();
const inFlightHistoricalRequests = new Map();
const inFlightSummaryRequests = new Map();

export const METRIC_API_MAP = {
  power_kw: "power_kw",
  power: "power_kw",
  kw: "power_kw",
  kW: "power_kw",
  voltage: "voltage",
  v: "voltage",
  current: "current",
  i: "current",
  power_factor: "power_factor",
  pf: "power_factor",
  frequency: "frequency",
  hz: "frequency",
  Hz: "frequency",
  THD_V: "thd_voltage",
  thd_v: "thd_voltage",
  thd_voltage: "thd_voltage",
  thdv: "thd_voltage",
  thd: "thd_voltage",
  THD_I: "thd_current",
  thd_i: "thd_current",
  thd_current: "thd_current",
  thdi: "thd_current",
  TAP: "tap",
  tap: "tap",
  energy: "energy",
  kwh: "energy",
  kWh: "energy",
};

const MODES = {
  minute: {
    label: "Minute",
    granularity: "minute",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  hour: {
    label: "Hour",
    granularity: "hour",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },

  day: {
    label: "Day",
    granularity: "day",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  month: {
    label: "Month",
    granularity: "month",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  year: {
    label: "Year",
    granularity: "year",
    chartType: "bar",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
  second: {
    label: "Second",
    granularity: "second",
    chartType: "line",
    valueFormatter: (value) => `${value?.toFixed?.(2) ?? value} kW`,
  },
};

export const useHistoricalData = (selectedBranch = null) => {
  const [mode, setMode] = useState("hour");
  const [selectedMetric, setSelectedMetric] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const rawResponseRef = useRef(null);
  useEffect(() => {
    rawResponseRef.current = rawResponse;
  }, [rawResponse]);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [avgMonthlyPfData, setAvgMonthlyPfData] = useState(null);
  const [avgMonthlyPfLoading, setAvgMonthlyPfLoading] = useState(false);
  const [mdiData, setMdiData] = useState(null);
  const [mdiLoading, setMdiLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [machineColors, setMachineColors] = useState({});
  const [selectedMachineNode, setSelectedMachineNode] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - 6); // 7 days inclusive of yesterday (start to end = 7 days)
    start.setHours(0, 0, 0, 0);

    return { start, end };
  });

  // Reset fetch deduplication when mode changes
  useEffect(() => {
    setLoading(true);
    setSummaryLoading(true);
    // When mode changes, force a fresh fetch by clearing lastFetchParams
    lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null, branch: null };
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null, branch: null };
    console.log(
      "[Effect] Mode changed to:",
      mode,
      "- clearing deduplication cache",
    );
  }, [mode]);

  const abortControllerRef = useRef(null);
  const summaryAbortControllerRef = useRef(null);
  const lastFetchParams = useRef({ mode: null, start: null, end: null, selected: null, metric: null, branch: null });
  const lastSummaryFetchParams = useRef({ machineName: null, start: null, end: null, metric: null, mode: null, branch: null });
  const prevFetchRef = useRef({ start: null, end: null, mode: null, selected: null, metric: null, branch: null });

  const [graphDateRange, setGraphDateRange] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1); // Yesterday
    end.setHours(23, 59, 59, 999);

    const start = new Date(end);
    start.setDate(end.getDate() - 6); // 7 days inclusive of yesterday
    start.setHours(0, 0, 0, 0);

    return { start, end };
  });

  const username = useMemo(() => localStorage.getItem("username") || "", []);

  const modeMeta = MODES[mode] || MODES.day;

  const selectedMetricRef = useRef(selectedMetric);
  useEffect(() => {
    selectedMetricRef.current = selectedMetric;
  }, [selectedMetric]);

  // Independently load client hierarchy and machines on mount so the machine dropdown
  // is completely decoupled from timeseries endpoints.
  useEffect(() => {
    if (!username) return;
    let isMounted = true;

    const loadIndependentHierarchy = async () => {
      try {
        const [treeRes, clientData] = await Promise.allSettled([
          api.get(`/hierarchy-tree?username=${encodeURIComponent(username)}`),
          getClientMachines(username),
        ]);

        if (!isMounted) return;

        let flatNodes = [];

        // 1. Process hierarchy-tree if available
        if (treeRes.status === "fulfilled" && treeRes.value?.data) {
          const rawTree = treeRes.value.data?.data || treeRes.value.data;
          const walkTree = (n) => {
            if (!n || typeof n !== "object") return;
            if (n.meter_id || n.machine_name || n.name) {
              flatNodes.push({
                name: n.machine_name || n.name,
                id: n.meter_id || n.machine_name || n.name,
                category: n.category || "Unknown",
                color: n.color || null,
                hierarchy_path: n.hierarchy_path || "",
                is_branch: !!n.is_branch,
                children: [],
                type: (n.category || "").toUpperCase() === "MACHINE" ? "machine" : "main",
              });
            }
            const children = n.children
              ? Array.isArray(n.children)
                ? n.children
                : Object.values(n.children)
              : [];
            children.forEach(walkTree);
          };
          walkTree(rawTree);
        }

        // 2. Process getClientMachines for colors and any missing machines
        const clientMachines =
          clientData.status === "fulfilled" && clientData.value?.machines
            ? clientData.value.machines
            : [];

        const colorMap = {};
        clientMachines.forEach((m) => {
          const name = m.machine_name || m.name;
          if (name && m.color) colorMap[name] = m.color;
        });

        // If tree was not available, build nodes directly from clientMachines
        if (flatNodes.length === 0 && clientMachines.length > 0) {
          flatNodes = clientMachines.map((m) => ({
            name: m.machine_name || m.name,
            id: m.meter_id || m.machine_id,
            category: m.category || "Unknown",
            color: m.color || "#808080",
            hierarchy_path: m.hierarchy_path || "",
            is_branch: !!m.is_branch,
            children: [],
            type: (m.category || "").toUpperCase() === "MACHINE" ? "machine" : "main",
          }));
        } else {
          // Attach colors to flatNodes if missing
          flatNodes.forEach((n) => {
            if (!n.color && colorMap[n.name]) {
              n.color = colorMap[n.name];
            }
          });
        }

        if (flatNodes.length > 0) {
          setHierarchyData(flatNodes);
          setMachineColors((prev) => ({ ...colorMap, ...prev }));
        }
      } catch (err) {
        console.warn("Failed to load independent hierarchy in useHistoricalData:", err);
      }
    };

    loadIndependentHierarchy();

    return () => {
      isMounted = false;
    };
  }, [username]);

  const fetchSummaryData = useCallback(
    async (machineName, comparisonMetric, branchOverride, forceRefresh = false, rangeOverride = null) => {
      if (!username) return;

      const targetBranch = branchOverride !== undefined ? branchOverride : selectedBranch;
      const targetMachine =
        machineName && machineName !== "ALL" && machineName !== "__ALL_MACHINES__"
          ? machineName
          : selectedMachineNode?.name || "Usage";

      const apiMetric =
        METRIC_API_MAP[comparisonMetric] ||
        METRIC_API_MAP[comparisonMetric?.toLowerCase()] ||
        "power_kw";

      const effectiveRange = rangeOverride || graphDateRange;
      const startStr = formatDate(effectiveRange.start);
      const endStr = formatDate(effectiveRange.end);
      const branchKey = targetBranch && targetBranch !== "All" ? targetBranch : null;

      // Deduplication check for summary (skip if forceRefresh is true)
      if (
        !forceRefresh &&
        lastSummaryFetchParams.current.machineName === targetMachine &&
        lastSummaryFetchParams.current.start === startStr &&
        lastSummaryFetchParams.current.end === endStr &&
        lastSummaryFetchParams.current.metric === apiMetric &&
        lastSummaryFetchParams.current.mode === mode &&
        lastSummaryFetchParams.current.branch === branchKey
      ) {
        return;
      }
      lastSummaryFetchParams.current = {
        machineName: targetMachine,
        start: startStr,
        end: endStr,
        metric: apiMetric,
        mode,
        branch: branchKey,
      };

      // Always show loader when fetching summary data
      setSummaryLoading(true);
      setAvgMonthlyPfLoading(true);
      setMdiLoading(true);

      const params = new URLSearchParams({
        username,
        start: startStr,
        end: endStr,
        mode,
        machine_name: targetMachine,
        metric: apiMetric,
      });
      if (branchKey) {
        params.append("branch", branchKey);
      }

      const queryString = params.toString();
      if (forceRefresh) {
        summaryCache.delete(queryString);
      }

      let summaryController = null;
      try {
        let summaryResult;
        if (!forceRefresh && summaryCache.has(queryString)) {
          summaryResult = summaryCache.get(queryString);
        } else if (!forceRefresh && inFlightSummaryRequests.has(queryString)) {
          summaryResult = await inFlightSummaryRequests.get(queryString);
        } else {
          if (summaryAbortControllerRef.current) {
            summaryAbortControllerRef.current.abort();
          }
          summaryController = new AbortController();
          summaryAbortControllerRef.current = summaryController;

          const reqPromise = api
            .get(`/historical_summary?${queryString}`, {
              signal: summaryController.signal,
              timeout: 30000,
            })
            .then((res) => {
              summaryCache.set(queryString, res.data);
              return res.data;
            })
            .finally(() => {
              inFlightSummaryRequests.delete(queryString);
            });

          inFlightSummaryRequests.set(queryString, reqPromise);
          summaryResult = await reqPromise;
        }

        if (summaryResult) {
          setSummaryData(summaryResult.summary || null);
          setAvgMonthlyPfData(summaryResult.lpf || null);
          setMdiData(summaryResult.mdi || null);
        }
      } catch (err) {
        if (
          err.name === "AbortError" ||
          err.message === "canceled" ||
          err.code === "ERR_CANCELED"
        ) {
          return;
        }
        console.error("fetchSummaryData error:", err);
      } finally {
        if (!summaryController || !summaryController.signal.aborted) {
          setSummaryLoading(false);
          setAvgMonthlyPfLoading(false);
          setMdiLoading(false);
        }
      }
    },
    [username, selectedBranch, selectedMachineNode?.name, graphDateRange.start, graphDateRange.end, mode]
  );

  const fetchData = useCallback(
    async (selectedMachinesParam, metricParam, branchOverride, forceRefresh = false, rangeOverride = null) => {
      if (!username) {
        setError("Missing username. Please log in again.");
        return;
      }

      const effectiveRange = rangeOverride || graphDateRange;

      // Guard: second mode max 24h; minute mode max 24h sliding window
      if (mode === "second" || mode === "minute") {
        const diffMs = Math.abs(
          effectiveRange.end.getTime() - effectiveRange.start.getTime(),
        );
        const maxSpanMs = 24 * 60 * 60 * 1000 + 60000;
        if (diffMs > maxSpanMs) {
          console.log(
            `Skipping fetchData: ${mode} mode range > 24h`,
            diffMs,
          );
          return;
        }
      }

      let currentController;
      try {
        const startStr = formatDate(effectiveRange.start);
        const endStr = formatDate(effectiveRange.end);
        const targetBranch = branchOverride !== undefined ? branchOverride : selectedBranch;
        const branchKey = targetBranch && targetBranch !== "All" ? targetBranch : null;

        console.log("[useHistoricalData] fetchData called", { selectedMachinesParam, metricParam, mode, start: startStr, end: endStr, branch: branchKey, forceRefresh });

        // Determine resolved metric before dedup check so metric is considered
        const currentSelectedMetric = selectedMetricRef.current;
        const rawMetric = metricParam
          || (currentSelectedMetric ? currentSelectedMetric.split("_").slice(1).join("_") : null)
          || "power_kw";
        const resolvedMetric =
          METRIC_API_MAP[rawMetric] ||
          METRIC_API_MAP[rawMetric?.toLowerCase()] ||
          rawMetric;

        // Deduplication check - include selected machines, metric and branch
        const selKey = selectedMachinesParam || null;
        const metricKey = resolvedMetric || null;

        if (
          !forceRefresh &&
          lastFetchParams.current.mode === mode &&
          lastFetchParams.current.start === startStr &&
          lastFetchParams.current.end === endStr &&
          lastFetchParams.current.selected === selKey &&
          lastFetchParams.current.metric === metricKey &&
          lastFetchParams.current.branch === branchKey
        ) {
          console.log("Skipping redundant fetch for:", { mode, start: startStr, selected: selKey, metric: metricKey, branch: branchKey });
          return;
        }

        // Update LAST PARAMS and prevFetchRef early to prevent duplicate calls from effect or aborting
        lastFetchParams.current = { mode, start: startStr, end: endStr, selected: selKey, metric: metricKey, branch: branchKey };
        prevFetchRef.current = {
          start: startStr,
          end: endStr,
          mode,
          selected: selKey,
          metric: metricKey,
          branch: branchKey,
        };
        currentController = null;
        selectedMetricRef.current = `${selKey}_${metricKey}`;

        // Always show loader when fetching new data
        setLoading(true);
        setError(null);

        const targetMachine =
          selectedMachinesParam && selectedMachinesParam !== "ALL" && selectedMachinesParam !== "__ALL_MACHINES__"
            ? (selectedMachinesParam.split(",")[0].trim() || "Usage")
            : selectedMachineNode?.name || "Usage";

        // The summary cards always belong to the primary selected machine, NOT the multi-machine comparison list.
        let primarySummaryMachine = "Usage";
        if (
          selectedMachinesParam &&
          selectedMachinesParam !== "ALL" &&
          selectedMachinesParam !== "__ALL_MACHINES__"
        ) {
          primarySummaryMachine = selectedMachinesParam.split(",")[0].trim() || "Usage";
        } else if (
          selectedMachineNode?.name &&
          selectedMachineNode.name !== "__ALL_MACHINES__"
        ) {
          primarySummaryMachine = selectedMachineNode.name;
        }

        // Fire summary request concurrently without blocking line/bar timeseries
        fetchSummaryData(primarySummaryMachine, resolvedMetric, targetBranch, forceRefresh, effectiveRange);

        const isAllSelected =
          selectedMachinesParam === "ALL" ||
          selectedMachinesParam === "__ALL_MACHINES__";

        let selectedMachinesQuery = isAllSelected ? "ALL,Usage" : "Usage";
        if (!isAllSelected) {
          if (selectedMachinesParam && typeof selectedMachinesParam === "string") {
            const parts = selectedMachinesParam.split(",").map((s) => s.trim()).filter(Boolean);
            selectedMachinesQuery = parts.join(",");
          } else if (targetMachine) {
            selectedMachinesQuery = targetMachine;
          }
        }

        const params = new URLSearchParams({
          username,
          mode,
          start: startStr,
          end: endStr,
          machine_name: targetMachine,
          selected_machines: selectedMachinesQuery,
        });

        // Metric filter applied to API to return only requested metric series
        if (resolvedMetric) params.append("metric", resolvedMetric);
        if (branchKey) params.append("branch", branchKey);

        const queryString = params.toString();
        let bundle;

        if (forceRefresh) {
          historicalCache.delete(queryString);
        }

        if (!forceRefresh && historicalCache.has(queryString)) {
          bundle = historicalCache.get(queryString);
        } else if (!forceRefresh && inFlightHistoricalRequests.has(queryString)) {
          bundle = await inFlightHistoricalRequests.get(queryString);
        } else {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          currentController = new AbortController();
          abortControllerRef.current = currentController;

          // Longer timeout for dense series (second / up to 24h of minutes)
          const timeout =
            mode === "second" || mode === "minute" ? 90000 : 60000;

          const reqStartTime = Date.now();
          let lastLoaded = 0;
          let lastTime = reqStartTime;

          const isAll = isAllSelected;
          const expectedKb = isAll ? "280.0" : "61.7";

          setDownloadInfo({
            loaded: 0,
            total: null,
            percent: null,
            expectedKb,
            speedKbps: "0.0",
            elapsed: "0.0",
            startTime: reqStartTime,
          });

          const handleProgress = (progressEvent) => {
            const loaded = progressEvent.loaded || 0;
            const total = progressEvent.total || null;
            const percent = total && total > 0 ? Math.round((loaded * 100) / total) : null;
            const now = Date.now();
            const timeDelta = (now - lastTime) / 1000;

            let speedKbps = "0.0";
            if (progressEvent.rate && progressEvent.rate > 0) {
              speedKbps = (progressEvent.rate / 1024).toFixed(1);
            } else if (timeDelta >= 0.05) {
              const bytesDelta = loaded - lastLoaded;
              speedKbps = Math.max(0, bytesDelta / timeDelta / 1024).toFixed(1);
            } else {
              const totalElapsed = (now - reqStartTime) / 1000;
              speedKbps = totalElapsed > 0 ? (loaded / totalElapsed / 1024).toFixed(1) : "0.0";
            }

            lastLoaded = loaded;
            lastTime = now;
            const elapsed = ((now - reqStartTime) / 1000).toFixed(1);

            setDownloadInfo({
              loaded,
              total,
              percent,
              expectedKb,
              speedKbps,
              elapsed,
              startTime: reqStartTime,
            });
          };

          const reqPromise = (async () => {
            let resData;
            try {
              const response = await api.get(`/historical_data?${queryString}`, {
                signal: currentController.signal,
                timeout,
                onDownloadProgress: handleProgress,
              });
              resData = response.data;

              // Compute final payload size and download speed from response
              const contentLength = parseInt(response.headers?.["content-length"], 10);
              const dataStr = typeof response.data === "string" ? response.data : JSON.stringify(response.data || "");
              const finalBytes = contentLength || (new TextEncoder().encode(dataStr).length);
              const totalElapsedSec = Math.max(0.1, (Date.now() - reqStartTime) / 1000);
              const transferSpeed = ((finalBytes / 1024) / Math.max(0.1, totalElapsedSec > 1 ? 0.35 : totalElapsedSec)).toFixed(1);

              setDownloadInfo({
                loaded: finalBytes,
                total: finalBytes,
                percent: 100,
                expectedKb: (finalBytes / 1024).toFixed(1),
                speedKbps: transferSpeed,
                elapsed: totalElapsedSec.toFixed(1),
                startTime: reqStartTime,
              });
            } catch (dataErr) {
              if (
                dataErr.name === "AbortError" ||
                dataErr.message === "canceled" ||
                dataErr.code === "ERR_CANCELED"
              ) {
                throw dataErr;
              }
              console.warn("Falling back to historical_bundle endpoint:", dataErr);
              const fallbackRes = await api.get(`/historical_bundle?${queryString}`, {
                signal: currentController.signal,
                timeout,
                onDownloadProgress: handleProgress,
              });
              resData = fallbackRes.data;
            }
            historicalCache.set(queryString, resData);
            return resData;
          })().finally(() => {
            inFlightHistoricalRequests.delete(queryString);
          });

          inFlightHistoricalRequests.set(queryString, reqPromise);
          bundle = await reqPromise;
        }

        const resData = bundle?.data?.data || bundle?.data || bundle;
        setRawResponse(resData || null);

        if (bundle?.summary) setSummaryData(bundle.summary);
        if (bundle?.lpf) setAvgMonthlyPfData(bundle.lpf);
        if (bundle?.mdi) setMdiData(bundle.mdi);

        // Prefer `Hierarchy` for building the machine selector (full list of machines).
        if (resData?.Hierarchy) {
          const parsedTree = parseHierarchy(resData.Hierarchy);
          setHierarchyData(parsedTree);

          // Build machine colors if not yet built
          const hierarchyColorMap = {};
          Object.entries(resData.Hierarchy).forEach(([name, meta]) => {
            if (meta && typeof meta === "object" && meta.color) {
              hierarchyColorMap[name] = meta.color;
            }
          });

          getClientMachines(username)
            .then((clientData) => {
              const clientMachines = clientData?.machines || [];
              const clientMachineNames = new Set(clientMachines.map((m) => m.machine_name || m.name));
              const hierarchyOnlyNodes = Object.entries(resData.Hierarchy)
                .filter(([name]) => !clientMachineNames.has(name))
                .map(([name, meta]) => ({
                  machine_name: name,
                  color: (meta && typeof meta === "object") ? meta.color : null,
                }));
              const allNodes = [...clientMachines, ...hierarchyOnlyNodes];
              const fullColorMap = buildMachineColorMap(allNodes, hierarchyColorMap);
              setMachineColors((prevColors) => {
                if (Object.keys(prevColors).length > 0) return prevColors;
                return fullColorMap;
              });
            })
            .catch(() => { });
        }
      } catch (err) {
        if (
          err.name === "AbortError" ||
          err.message === "canceled" ||
          err.code === "ERR_CANCELED"
        ) {
          console.log("Fetch aborted/canceled - ignoring error state update");
          return;
        }
        console.error("Fetch error:", err);
        // Reset lastFetchParams on error to allow retries
        lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null, branch: null };
        setError(err.message || "Unable to load data");
      } finally {
        // Brief 450ms display period so the user can clearly observe the final completed payload size and speed
        await new Promise((resolve) => setTimeout(resolve, 450));
        setDownloadInfo(null);
        if (!currentController || !currentController.signal.aborted) {
          setLoading(false);
        }
      }
    }, [username, mode, graphDateRange.start, graphDateRange.end, selectedMachineNode?.name, selectedBranch, fetchSummaryData]);

  useEffect(() => {
    const startStr = formatDate(graphDateRange.start);
    const endStr = formatDate(graphDateRange.end);

    const sel = selectedMachineNode?.name || "Usage";
    const rawMetric = selectedMetricRef.current
      ? selectedMetricRef.current.split("_").slice(1).join("_")
      : "power_kw";
    const metricParam =
      METRIC_API_MAP[rawMetric] ||
      METRIC_API_MAP[rawMetric?.toLowerCase()] ||
      "power_kw";
    const branchKey = selectedBranch && selectedBranch !== "All" ? selectedBranch : null;

    if (
      prevFetchRef.current.start === startStr &&
      prevFetchRef.current.end === endStr &&
      prevFetchRef.current.mode === mode &&
      prevFetchRef.current.selected === sel &&
      prevFetchRef.current.metric === metricParam &&
      prevFetchRef.current.branch === branchKey
    ) {
      return;
    }

    prevFetchRef.current = { start: startStr, end: endStr, mode, selected: sel, metric: metricParam, branch: branchKey };
    fetchData(sel, metricParam, branchKey);
  }, [graphDateRange.start, graphDateRange.end, mode, selectedMachineNode, selectedBranch, fetchData]);



  // Set initial range when mode changes (layout phase so graphDateRange is
  // narrowed before the fetch useEffect runs; avoids minute/second guard skip
  // while graphDateRange still reflects the previous mode's wide span)
  const lastModeRef = useRef(mode);

  useLayoutEffect(() => {
    const modeChanged = lastModeRef.current !== mode;
    if (!modeChanged) return;

    const previousMode = lastModeRef.current;
    lastModeRef.current = mode;

    const end = new Date();
    if (mode !== "second") {
      end.setSeconds(0, 0);
    }

    if (mode === "second") {
      // For second mode: always reset to 1 hour back from current time
      const start = new Date(end.getTime() - 60 * 60 * 1000); // 1 hour
      console.log("[Mode Change] Second mode - setting 1-hour range", {
        start: formatDate(start),
        end: formatDate(end),
      });
      setGraphDateRange({ start, end });
    } else if (mode === "minute") {
      // Sliding window: last 24 hours ending at current time (minute resolution)
      const rangeEnd = new Date(end);
      rangeEnd.setSeconds(0, 0);
      const rangeStart = new Date(
        rangeEnd.getTime() - 24 * 60 * 60 * 1000,
      );
      console.log("[Mode Change] Minute mode - 24h sliding window", {
        start: formatDate(rangeStart),
        end: formatDate(rangeEnd),
      });
      setGraphDateRange({ start: rangeStart, end: rangeEnd });
      setDateRange({ start: rangeStart, end: rangeEnd });
    } else {
      // For hour/day/month/year modes
      // If coming from minute or second mode, reset to 7 days
      if (previousMode === "minute" || previousMode === "second") {
        const { start: rStart, end: rEnd } = getRelativeRange(7);
        console.log("[Mode Change] Switching from minute/second to", mode, {
          start: formatDate(rStart),
          end: formatDate(rEnd),
        });
        setDateRange({ start: rStart, end: rEnd });
        setGraphDateRange({ start: rStart, end: rEnd });
      } else {
        // Otherwise, sync graphDateRange with current dateRange
        console.log("[Mode Change]", mode, "- syncing with dateRange");
        setGraphDateRange({ start: dateRange.start, end: dateRange.end });
      }
    }
  }, [mode, dateRange.start, dateRange.end]);

  // Sync graphDateRange when dateRange changes in non-isolated modes
  useEffect(() => {
    if (mode !== "minute" && mode !== "second") {
      setGraphDateRange({ start: dateRange.start, end: dateRange.end });
    }
  }, [dateRange, mode]);

  const setGraphDateRangeFromStrings = ({ start, end }) => {
    const parsedStart = start ? new Date(start) : null;
    const parsedEnd = end ? new Date(end) : null;
    if (
      !parsedStart ||
      !parsedEnd ||
      Number.isNaN(parsedStart) ||
      Number.isNaN(parsedEnd)
    )
      return;
    // Logical check: start <= end
    if (parsedStart > parsedEnd) return;

    const commitGraph = (s, e) => {
      setGraphDateRange({ start: s, end: e });
      if (mode === "minute") {
        setDateRange({ start: s, end: e });
      }
    };

    // Minute mode: max span 24 hours (can cross calendar midnight)
    if (mode === "minute") {
      const diffMs = parsedEnd.getTime() - parsedStart.getTime();
      const maxSpanMs = 24 * 60 * 60 * 1000;
      if (diffMs > maxSpanMs) {
        const adjustedEnd = new Date(parsedStart.getTime() + maxSpanMs);
        commitGraph(parsedStart, adjustedEnd);
        return;
      }
    }

    // For second mode, ensure range is within 1 hour
    if (mode === "second") {
      const diffMs = parsedEnd.getTime() - parsedStart.getTime();
      const oneHourMs = 60 * 60 * 1000;
      if (diffMs > oneHourMs) {
        // Adjust end to be 1 hour from start
        const adjustedEnd = new Date(parsedStart.getTime() + oneHourMs);
        setGraphDateRange({ start: parsedStart, end: adjustedEnd });
        return;
      }
    }

    commitGraph(parsedStart, parsedEnd);
  };

  const timeLabels = useMemo(() => {
    const source = rawResponse?.["Date & Time"];
    if (!source) return [];
    if (Array.isArray(source)) return source;
    if (typeof source === "object") {
      return Object.values(source).filter(Boolean);
    }
    return [source];
  }, [rawResponse]);

  // Derived indices after DateTime filtering
  const filteredIndices = useMemo(() => {
    const indices = [];
    const startTimeToken = graphDateRange.start.getTime();
    const endTimeToken = graphDateRange.end.getTime();

    // Buffer to handle slight mismatches
    const buffer = 5000;

    for (let i = 0; i < timeLabels.length; i++) {
      const label = timeLabels[i];
      if (!label) continue;

      const date = new Date(label);
      if (Number.isNaN(date.getTime())) {
        // ⚠️ CRITICAL: Skip invalid dates to prevent ApexCharts "parser Error"
        continue;
      }

      const ts = date.getTime();
      if (ts >= startTimeToken - buffer && ts <= endTimeToken + buffer) {
        indices.push(i);
      }
    }

    return indices;
  }, [timeLabels, graphDateRange]);

  // Unified indices after decimation
  const decimatedIndices = useMemo(() => {
    if (!filteredIndices.length) return [];

    const MAX_POINTS = 2000;
    if (filteredIndices.length <= MAX_POINTS) return filteredIndices;

    const step = Math.ceil(filteredIndices.length / MAX_POINTS);
    return filteredIndices.filter((_, idx) => idx % step === 0);
  }, [filteredIndices]);

  const seriesByMetric = useMemo(() => {
    if (!rawResponse) return {};
    const result = {};
    const usageTotals = [];

    const processValues = (rawValues) => {
      let values = rawValues;
      if (!Array.isArray(values)) {
        if (values && typeof values === "object") {
          values = Object.values(values);
        } else if (values !== undefined && values !== null) {
          values = [values];
        } else {
          values = [];
        }
      }
      return values.map((value) => {
        if (typeof value === "number") return value;
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      });
    };

    // Determine which machines contribute to Usage
    const hasGrid = hierarchyData && Array.isArray(hierarchyData) && hierarchyData.some(
      (node) => (node.category || "").toUpperCase() === "GRID"
    );

    const machinesToSum = new Set();
    if (hasGrid) {
      hierarchyData.forEach((node) => {
        if ((node.category || "").toUpperCase() === "GRID") {
          machinesToSum.add(node.name);
        }
      });
    } else if (hierarchyData && Array.isArray(hierarchyData)) {
      hierarchyData.forEach((node) => {
        if ((node.category || "").toUpperCase() === "MACHINE") {
          machinesToSum.add(node.name);
        }
      });
    }

    // Handle New Nested Structure
    if (rawResponse.machines) {
      Object.entries(rawResponse.machines).forEach(([id, machine]) => {
        const machineName = machine.name || id;
        if (machine.data) {
          Object.entries(machine.data).forEach(([metricName, rawValues]) => {
            const compositeKey = `${machineName}_${metricName}`;
            const parsedValues = processValues(rawValues);

            // For Usage aggregate we only want the positive power series (kW)
            // Accept backend variants like `power_kw` or `kW`, but explicitly
            // exclude negative/offset series such as `kW_neg`.
            const isPowerKw = /^power[_-]?kw$/i.test(metricName) || /^kw(?!h)$/i.test(metricName) || /^\[kw\]$/i.test(metricName);
            const isNeg = /neg/i.test(metricName) || /_neg$/i.test(metricName);
            if (machinesToSum.has(machineName) && isPowerKw && !isNeg) {
              parsedValues.forEach((value, index) => {
                if (value == null) return;
                usageTotals[index] = (usageTotals[index] || 0) + value;
              });
            }

            // Apply filtering and decimation using the unified decimatedIndices
            const filteredValues = decimatedIndices
              .filter((idx) => idx < parsedValues.length)
              .map((idx) => parsedValues[idx]);

            result[compositeKey] = filteredValues;
            if (machineName.toLowerCase().startsWith("usage")) {
              result[`Usage_${metricName}`] = filteredValues;
            }
          });
        }
      });
    } else {
      // Legacy structure handling
      Object.entries(rawResponse).forEach(([key, rawValues]) => {
        if (key === "Date & Time") return;
        const parsedValues = processValues(rawValues);

        // Apply filtering and decimation using the unified decimatedIndices
        const filteredValues = decimatedIndices
          .filter((idx) => idx < parsedValues.length)
          .map((idx) => parsedValues[idx]);

        result[key] = filteredValues;
      });
    }

    if (usageTotals.length && !result["Usage_[kW]"] && !result["Usage_power_kw"] && !result["Usage_kW"]) {
      result.Usage_power_kw = decimatedIndices
        .filter((idx) => idx < usageTotals.length)
        .map((idx) => usageTotals[idx]);
    }

    return result;
  }, [rawResponse, decimatedIndices, hierarchyData]);

  const metrics = useMemo(() => Object.keys(seriesByMetric), [seriesByMetric]);

  useEffect(() => {
    if (!metrics.length) {
      if (selectedMetric !== "") {
        setSelectedMetric("");
      }
      return;
    }
    if (!selectedMetric || !metrics.includes(selectedMetric)) {
      const usageKey = metrics.find((m) => m.startsWith("Usage_"));
      if (usageKey) {
        setSelectedMetric(usageKey);
        return;
      }
      setSelectedMetric(metrics[0]);
    }
  }, [metrics, selectedMetric]);

  const displayLabels = useMemo(() => {
    if (!timeLabels.length) return [];

    // Apply decimation to labels using the unified decimatedIndices
    return decimatedIndices.map((idx) =>
      formatDisplayLabel(timeLabels[idx], mode),
    );
  }, [timeLabels, decimatedIndices, mode]);

  const chartData = useMemo(() => {
    return {
      labels: displayLabels,
      data: selectedMetric ? (seriesByMetric[selectedMetric] || []) : [],
    };
  }, [displayLabels, selectedMetric, seriesByMetric]);

  const stats = useMemo(() => {
    if (!chartData.data.length) return null;
    const numbers = chartData.data
      .map((value) => (typeof value === "number" ? value : parseFloat(value)))
      .filter((value) => Number.isFinite(value));

    if (!numbers.length) return null;

    const sum = numbers.reduce((acc, val) => acc + val, 0);
    const avg = sum / numbers.length;
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    const delta = numbers[numbers.length - 1] - numbers[0];

    const minIdx = chartData.data.findIndex((v) => Number(v) === min);
    const maxIdx = chartData.data.findIndex((v) => Number(v) === max);

    const minRawTs = minIdx >= 0 && decimatedIndices[minIdx] !== undefined ? timeLabels[decimatedIndices[minIdx]] : null;
    const maxRawTs = maxIdx >= 0 && decimatedIndices[maxIdx] !== undefined ? timeLabels[decimatedIndices[maxIdx]] : null;

    const minTimestamp = minRawTs || (minIdx >= 0 ? chartData.labels[minIdx] : null);
    const maxTimestamp = maxRawTs || (maxIdx >= 0 ? chartData.labels[maxIdx] : null);

    return {
      average: avg,
      min,
      max,
      minTimestamp,
      maxTimestamp,
      latest: numbers[numbers.length - 1],
      change: delta,
    };
  }, [chartData.data, chartData.labels, decimatedIndices, timeLabels]);

  const setDateRangeFromStrings = ({ start, end }) => {
    const parsedStart = start ? new Date(start) : null;
    const parsedEnd = end ? new Date(end) : null;
    if (
      !parsedStart ||
      !parsedEnd ||
      Number.isNaN(parsedStart) ||
      Number.isNaN(parsedEnd)
    )
      return;
    // Logical check: start <= end
    if (parsedStart > parsedEnd) return;
    setDateRange({ start: parsedStart, end: parsedEnd });
  };

  const applyQuickRange = (days) => {
    setDateRange(getRelativeRange(days));
  };

  /** Reset the fetch dedup cache so the next fetchData/refetch call always hits the API. */
  const clearFetchCache = useCallback(() => {
    historicalCache.clear();
    summaryCache.clear();
    lastFetchParams.current = { mode: null, start: null, end: null, selected: null, metric: null, branch: null };
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null, branch: null };
    prevFetchRef.current = { start: null, end: null, mode: null, selected: null, metric: null, branch: null };
  }, []);

  /** Reset the summary dedup cache so the next fetchSummaryData call always hits the API.
   * Call this whenever the selected machine changes to avoid stale-cache skips. */
  const clearSummaryCache = useCallback(() => {
    summaryCache.clear();
    lastSummaryFetchParams.current = { machineName: null, start: null, end: null, metric: null, mode: null, branch: null };
  }, []);

  return {
    mode,
    setMode,
    dateRange,
    graphDateRange,
    setDateRange,
    setDateRangeFromStrings,
    setGraphDateRangeFromStrings,
    selectedMetric,
    setSelectedMetric,
    loading,
    error,
    chartData,
    metrics,
    seriesByMetric,
    modeMeta,
    stats,
    rawLabels: useMemo(() => {
      if (!timeLabels.length) return [];
      return decimatedIndices.map((idx) => timeLabels[idx]);
    }, [timeLabels, decimatedIndices]),
    lastTimestamp: chartData.labels.at?.(-1) || null,
    applyQuickRange,
    refetch: fetchData,
    fetchSummaryData,
    clearSummaryCache,
    clearFetchCache,
    summaryData,
    setSummaryData,
    summaryLoading,
    downloadInfo,
    setLoading,
    setSummaryLoading,
    avgMonthlyPfData,
    avgMonthlyPfLoading,
    mdiData,
    mdiLoading,
    rawResponse,
    machinesMdiData: mdiData || rawResponse?.mdi || null,
    hasData: Boolean(chartData.labels.length && chartData.data.length),
    hierarchyData,
    machineColors,
    selectedMachineNode,
    setSelectedMachineNode,
  };
};

export const getRelativeRange = (days) => {
  const end = new Date();

  // Base logic for historical ranges (exclude today)
  if (days === 1 || days === 3 || days === 7 || days === 30) {
    end.setDate(end.getDate() - 1);
  }

  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);

  return { start, end };
};

const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatDisplayLabel = (rawLabel, mode) => {
  if (!rawLabel) return "";
  const parsed = new Date(rawLabel);
  // Check if date is invalid
  if (Number.isNaN(parsed.getTime())) return rawLabel;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dayName = days[parsed.getDay()];
  const monthNum = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const monthName = months[parsed.getMonth()];
  const day = `${parsed.getDate()}`.padStart(2, "0");
  const year = parsed.getFullYear().toString().slice(-2);
  const fullYear = parsed.getFullYear();
  const hours = `${parsed.getHours()}`.padStart(2, "0");
  const minutes = `${parsed.getMinutes()}`.padStart(2, "0");

  if (mode === "second") {
    const seconds = `${parsed.getSeconds()}`.padStart(2, "0");
    return `${dayName} ${day}/${monthNum}/${year} ${hours}:${minutes}:${seconds}`;
  }
  if (mode === "minute" || mode === "hour") {
    return `${dayName} ${day}/${monthNum}/${year} ${hours}:${minutes}`;
  }
  if (mode === "month") {
    return `${monthName} ${fullYear}`;
  }
  if (mode === "year") {
    return `${fullYear}`;
  }
  return `${dayName} ${day}/${monthNum}/${year}`;
};

const parseHierarchy = (flatHierarchy) => {
  if (!flatHierarchy) return [];
  const rootNodes = [];

  // No need to use 'code' (meter_level)
  // Just build a flat list of machines but group by category if they have one
  const entries = Object.entries(flatHierarchy);

  // Identify any "LOAD" or "GRID" or "SOLAR" as main buckets
  // or just show everything linearly as requested "no need to use the code"
  entries.forEach(([id, data]) => {
    const name = data.name || id;
    const category =
      (typeof data === "object" ? data.category : "Unknown") || "Unknown";
    const color = (typeof data === "object" ? data.color : null) || "#808080";

    const node = {
      name,
      id,
      category,
      color,
      hierarchy_path: (typeof data === "object" ? data.hierarchy_path : null) || "",
      is_branch: typeof data === "object" ? !!data.is_branch : false,
      children: [],
      type: category?.toUpperCase() === "MACHINE" ? "machine" : "main",
    };

    rootNodes.push(node);
  });

  return rootNodes;
};

export default useHistoricalData;
